import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useGuardedAsyncCallback from 'src/hooks/useGuardedAsyncCallback';
import { useContextSelector } from 'use-context-selector';
import AddToFavoritesDropdown from './AddToFavoritesDropdown';
import ExternalCatalogButton from './ExternalCatalogButton';
import ExternalProductFilterDropdown from './ExternalProductFilterDropdown';
import { ViewCatalogProductsContext } from './ViewCatalogProductsContext';

interface ProductToolbarActionsProps {
  showExternalProductFilter?: boolean;
  className?: string;
  rightContent?: ReactNode;
}

const ProductToolbarActions: React.FC<ProductToolbarActionsProps> = ({
  showExternalProductFilter = true,
  className = '',
  rightContent,
}) => {
  const { t } = useTranslation();
  const selectedCatalog = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.selectedCatalog
  );
  const hasSelectedItems = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.hasSelectedItems
  );

  const folderOptions = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.folderOptions
  );

  const handleAddToFavorites = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleAddToFavorites
  );

  const isAddingToFavorites = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isAddingToFavorites
  );
  const isUpdatingCart = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isUpdatingCart
  );

  const clearFilters = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.clearFilters
  );

  const hasActiveFilters = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.hasActiveFilters
  );

  const isExternalCatalog = useMemo(
    () => selectedCatalog && selectedCatalog.catalogType === 2,
    [selectedCatalog]
  );

  const handleSelectedItemsAddToCart = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.handleSelectedItemsAddToCart
  );

  const [handleAddToCartClick, isAddToCartPending] = useGuardedAsyncCallback(
    handleSelectedItemsAddToCart
  );
  const isSupplierUser = useContextSelector(
    ViewCatalogProductsContext,
    (context) => context.isSupplierUser
  );

  const isCartActionPending = isUpdatingCart || isAddToCartPending;
  const isAnyActionPending = isCartActionPending || isAddingToFavorites;

  return (
    <div className={`flex w-full flex-row flex-wrap items-center gap-x-2 gap-y-2 ${className}`}>
      <div className="flex min-w-0 flex-row flex-wrap items-center gap-1">
        <ClearFilterButton onClick={clearFilters} disabled={!hasActiveFilters || isAnyActionPending} />
        {isExternalCatalog ? (
          <>
            <ExternalCatalogButton />
            <p className="text-sm text-white">{t('openExternalCatalogMessage')}</p>
          </>
        ) : !isSupplierUser ? (
          <>
            <AddToFavoritesDropdown
              folders={folderOptions || []}
              onAdd={handleAddToFavorites}
              disabled={!hasSelectedItems}
              isBusy={isAnyActionPending && !isAddingToFavorites}
              isLoading={isAddingToFavorites}
            />
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper
                    type="Information"
                    contents={[t('Please select at least one item to Add to Cart')]}
                  />
                </div>
              }
              show={!hasSelectedItems}
            >
              <Button
                color="success"
                size="md"
                disabled={!hasSelectedItems || (isAnyActionPending && !isCartActionPending)}
                loading={isCartActionPending}
                onClick={handleAddToCartClick}
              >
                {t('Add to Cart')}
              </Button>
            </PopUp>
          </>
        ) : null}
      </div>
      {rightContent !== undefined ? (
        <div className="ml-auto flex min-w-0 flex-row flex-wrap items-center gap-2">
          {rightContent}
        </div>
      ) : (
        showExternalProductFilter && (
          <div className="ml-auto flex min-w-0 flex-row flex-wrap items-center justify-end gap-2">
            <ExternalProductFilterDropdown />
          </div>
        )
      )}
    </div>
  );
};

export default ProductToolbarActions;
